import { useEffect, useRef, useState } from "react";
import {
  LIVE_GAMES,
  GAME_GROUP_LABELS,
  resolveGameHref,
  type GameDefinition,
  type GameGroup,
} from "@ziolo/shared";

export interface NavbarProps {
  /** Defaults to the full registry. Pass a subset to scope the menu. */
  games?: readonly GameDefinition[] | undefined;
  homeHref?: string | undefined;
  /** Shown next to Menu when the signed-in user is an admin. */
  adminHref?: string | undefined;
}

const GROUP_ORDER: GameGroup[] = ["casino", "cards", "party", "tool"];

export function Navbar({ games = LIVE_GAMES, homeHref = "/", adminHref }: NavbarProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);

  // Legacy behaviour: clicking anywhere outside closes the menus.
  useEffect(() => {
    if (!menuOpen && !mobileOpen) return;

    function onPointerDown(event: MouseEvent) {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
        setMobileOpen(false);
      }
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setMenuOpen(false);
        setMobileOpen(false);
      }
    }

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen, mobileOpen]);

  useEffect(() => {
    document.body.classList.toggle("z-nav-locked", mobileOpen);
    return () => document.body.classList.remove("z-nav-locked");
  }, [mobileOpen]);

  const grouped = GROUP_ORDER.map((group) => ({
    group,
    items: games.filter((game) => game.group === group),
  })).filter((entry) => entry.items.length > 0);

  return (
    <nav className="z-navbar" ref={navRef}>
      <div className="z-navbar__inner">
        <a href={homeHref} className="z-logo">
          zioło.eu
        </a>

        <ul className="z-nav-links">
          <li>
            <button
              type="button"
              className="z-nav-trigger"
              aria-expanded={menuOpen}
              aria-haspopup="true"
              onClick={() => setMenuOpen((open) => !open)}
            >
              Menu
            </button>
            <ul className={`z-dropdown ${menuOpen ? "z-dropdown--open" : ""}`}>
              {grouped.map(({ group, items }) => (
                <li key={group}>
                  <div className="z-dropdown__group-label">
                    {GAME_GROUP_LABELS[group]}
                  </div>
                  <ul>
                    {items.map((game) => (
                      <li key={game.id}>
                        <GameLink game={game} />
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </li>
          {adminHref ? (
            <li>
              <a href={adminHref}>Admin</a>
            </li>
          ) : null}
        </ul>

        <button
          type="button"
          className={`z-hamburger ${mobileOpen ? "z-hamburger--open" : ""}`}
          aria-label="Menu"
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen((open) => !open)}
        >
          <span />
          <span />
          <span />
        </button>
      </div>

      <div className={`z-mobile-menu ${mobileOpen ? "z-mobile-menu--open" : ""}`}>
        <ul>
          {adminHref ? (
            <li>
              <a href={adminHref}>Admin</a>
            </li>
          ) : null}
          {games.map((game) => (
            <li key={game.id}>
              <GameLink game={game} />
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
}

function GameLink({ game }: { game: GameDefinition }) {
  const href = resolveGameHref(game);
  // Legacy games live on the Apache vhost, outside the SPA router.
  const isLegacy = game.status !== "live";

  return (
    <a href={href} {...(isLegacy ? { "data-legacy": "true" } : {})}>
      <i className="z-icon" aria-hidden="true">
        {game.icon}
      </i>
      {game.label}
    </a>
  );
}
